import _cloneDeep from 'lodash.clonedeep'
import PresenceAvatarStack from '@/components/presence/PresenceAvatarStack.vue'
import { presenceProvider } from '@/services/presence'
import { PRESENCE_SESSION_ID } from '@/services/presence/session'
import EventHubTypes from '@/enums/EventHubTypes'
import AudaNetTaskTypes from '@/enums/AudaNetTaskTypes'
import QuoteRoutes from '../route-types'

// The PNET tab is relabelled by the sub-nav for AudaNet/AudaBridge quotes
// (QuoteSubNav.pnetAudanetMenuText) — presence reports the same text so peers
// see the tab name the user is actually looking at.
function pnetAudanetTabLabel(entity) {
  if (!entity?.isAudaNet) return 'PNET'
  const taskType = entity.audatex?.taskType
  if (entity.isAudaBridge) return taskType || AudaNetTaskTypes.AudaBridge
  return taskType ? `PNET/${taskType}` : 'PNET/AudaNet'
}

export const QuotePresenceMixin = {
  components: { PresenceAvatarStack },

  data() {
    return {
      presenceReady: false,
      quoteUpdatesUnsubscribe: null,
      quoteDeletedUnsubscribe: null,
      customerUpdatesUnsubscribe: null,
      vehicleUpdatesUnsubscribe: null,
      isQuoteDeletedByOtherActive: false,
      quoteDeletedByName: null
    }
  },

  computed: {
    isPresenceEnabled() {
      const setting = this.$company?.setting?.quotePresenceEnabled
      return typeof setting === 'boolean' ? setting : false
    },
    currentUserForPresence() {
      const info = this.$store.getters['userInfo/info']
      return {
        userId: info?.userId,
        firstName: info?.firstName,
        lastName: info?.lastName,
        displayName: info?.displayName,
        companyId: this.$company?.setting?.companyId
      }
    },
    activeTab() {
      const map = {
        QuoteHeader:         'Header',
        QuoteBuilder:        'Quoting',
        QuotePartsControl:   'Parts',
        QuoteImages:         'Images',
        AuthoriseView:       'Assessments',
        QuoteOrm:            'ORM',
        QuotePnetAudanet:    pnetAudanetTabLabel(this.entity),
        QuoteArnie:          'ARNIE',
        QuoteEstImage:       'EstImage',
        QuoteSummary:        'Summary',
        QuoteInvoice:        'Invoice',
        QuoteAssessmentInvoice: 'Invoice',
        QuoteExcessInvoice:  'Excess',
        QuoteDocuments:      'Docs',
        QuoteCommunications: 'Comms',
        QuoteRemarks:        'Remarks',
        QuoteRatesMarkups:   'Rates & Markups',
        QuoteAnnotations:    'Annotations',
        QuoteOtherLabour:    'Other Labour',
        QuoteRepx:           'RepX',
        QuotePriceParts:     'Price Parts'
      }
      return map[this.$route.name] || ''
    }
  },

  watch: {
    'entity.customerId'(newId, oldId) {
      if (newId === oldId || !this.presenceReady) return
      if (this.customerUpdatesUnsubscribe) { this.customerUpdatesUnsubscribe(); this.customerUpdatesUnsubscribe = null }
      if (newId) {
        this.customerUpdatesUnsubscribe = presenceProvider.subscribeToCustomerUpdates(newId, this._onCustomerUpdated)
      }
    },
    'entity.vehicleId'(newId, oldId) {
      if (newId === oldId || !this.presenceReady) return
      if (this.vehicleUpdatesUnsubscribe) { this.vehicleUpdatesUnsubscribe(); this.vehicleUpdatesUnsubscribe = null }
      if (newId) {
        this.vehicleUpdatesUnsubscribe = presenceProvider.subscribeToVehicleUpdates(newId, this._onVehicleUpdated)
      }
    }
  },

  created() {
    // No periodic entity refresh here — FQ-3204 removed it from QuoteView.vue, but this copy
    // survived without its clearInterval and leaked a timer per quote opened.
    this._presenceIsReloading = false
    this._presenceReloadPending = false
  },

  beforeDestroy() {
    this._unsubscribePresenceSubscriptions()
  },

  methods: {
    // An event is "our own" when it originated from this tab's presence session.
    // Session takes precedence over user: the same user in another tab is a
    // different session and must sync. The userId comparison is only a fallback
    // for older payloads that carry no session ID.
    _isOwnPresenceEvent(eventSessionId, eventUserId) {
      if (eventSessionId) return eventSessionId === PRESENCE_SESSION_ID
      return eventUserId === this.currentUserForPresence.userId
    },
    // A user-caused change (source "manual" or absent) is attributed to the person:
    // "{name} updated this quote." An autonomous integration change (source is an
    // integration name) has no user, so it reads "Quote updated by {integration}."
    _notifyPresenceUpdate(updaterName, source) {
      if (!this._presenceNotificationPending) {
        this._presenceNotificationPending = true
        const message = source && source !== 'manual'
          ? `Quote updated by ${source}.`
          : `${updaterName || 'Someone'} updated this quote.`
        this.$notification.openNotificationWithType('success', '', message, 3000)
        setTimeout(() => { this._presenceNotificationPending = false }, 3000)
      }
    },
    _unsubscribePresenceSubscriptions() {
      if (this.quoteUpdatesUnsubscribe) { this.quoteUpdatesUnsubscribe(); this.quoteUpdatesUnsubscribe = null }
      if (this.quoteDeletedUnsubscribe) { this.quoteDeletedUnsubscribe(); this.quoteDeletedUnsubscribe = null }
      if (this.customerUpdatesUnsubscribe) { this.customerUpdatesUnsubscribe(); this.customerUpdatesUnsubscribe = null }
      if (this.vehicleUpdatesUnsubscribe) { this.vehicleUpdatesUnsubscribe(); this.vehicleUpdatesUnsubscribe = null }
    },
    // Fired by PresenceAvatarStack on every (re)connect — including when the view is
    // reused for a different quote in the family (main ↔ additional) — so subscriptions
    // must be torn down and rebuilt for the current quoteId.
    onPresenceReady() {
      this.presenceReady = true
      this._unsubscribePresenceSubscriptions()
      this.quoteUpdatesUnsubscribe = this.isPresenceEnabled
        ? presenceProvider.subscribeToQuoteEntity(this.entity.quoteId, this._onQuoteEntityUpdated)
        : presenceProvider.subscribeToQuoteSaves(this.entity.quoteId, this._onQuoteUpdated)
      this.quoteDeletedUnsubscribe = presenceProvider.subscribeToQuoteDeleted(this.entity.quoteId, this._onQuoteDeleted)
      if (this.entity.customerId) {
        this.customerUpdatesUnsubscribe = presenceProvider.subscribeToCustomerUpdates(this.entity.customerId, this._onCustomerUpdated)
      }
      if (this.entity.vehicleId) {
        this.vehicleUpdatesUnsubscribe = presenceProvider.subscribeToVehicleUpdates(this.entity.vehicleId, this._onVehicleUpdated)
      }
    },
    _onQuoteDeleted({ deletedBy, deletedBySession, deletedByName }) {
      if (this._isOwnPresenceEvent(deletedBySession, deletedBy)) return
      // Stop reacting to further presence traffic for a quote that no longer exists.
      this._unsubscribePresenceSubscriptions()
      this.quoteDeletedByName = deletedByName || 'Someone'
      this.isQuoteDeletedByOtherActive = true
    },
    acknowledgeQuoteDeleted() {
      this.isQuoteDeletedByOtherActive = false
      // The quote is gone — discard local state so the unsaved-changes guard
      // doesn't block leaving.
      this.setBaseSnapshot(_cloneDeep(this.entity))
      this.saveSnapshot(_cloneDeep(this.entity))
      this.$router.push({ name: QuoteRoutes.QuoteListView.name })
    },
    _onQuoteUpdated({ savedBy, savedBySession, savedByName, source }) {
      if (this._isOwnPresenceEvent(savedBySession, savedBy)) return
      this._executePresenceReload(savedByName, source)
    },
    _onQuoteEntityUpdated({ savedBy, savedBySession, savedByName, lastSavedAt, source, ...entity }) { // eslint-disable-line no-unused-vars
      if (this._isOwnPresenceEvent(savedBySession, savedBy)) return

      // Firebase drops empty arrays (treats [] as null), so missing collection fields must be defaulted here.
      const ARRAY_FIELDS = ['labours', 'parts', 'opgs', 'miscs', 'sublets', 'others', 'rates', 'invoices', 'remarks', 'printedRemarks', 'jobNoteRemarks', 'otherRemarks', 'annotations', 'dueInNotes', 'dueOutNotes']
      ARRAY_FIELDS.forEach(function(f) { entity[f] = entity[f] || [] })

      entity.ormHistory       = this.entity?.ormHistory       ?? []
      entity.quoteAssessments = this.entity?.quoteAssessments ?? []

      const vehicleChanged  = entity.vehicleId  !== this.entity?.vehicleId
      const customerChanged = entity.customerId !== this.entity?.customerId
      const insurerChanged  = entity.insurerId  !== this.entity?.insurerId

      // Guard the autosave watchers while the entity is swapped in (same pattern as
      // the helper's debouncedEntityRefresh): the deep item watchers recompute derived
      // totals on the new arrays, and without the guard they schedule an items save
      // with no user interaction. Since every save broadcasts, two idle tabs would
      // otherwise re-trigger each other in an endless save/apply loop.
      this._isAutosaveRefreshing = true

      this.setBaseSnapshot(entity)
      this.saveSnapshot(entity)
      this.entity = _cloneDeep(entity)

      const headerPromises = []
      if (vehicleChanged  && entity.vehicleId)  headerPromises.push(this.getQuoteVehicle({ id: entity.vehicleId,  refresh: true }))
      if (customerChanged && entity.customerId) headerPromises.push(this.getQuoteCustomer({ id: entity.customerId, refresh: true }))
      if (insurerChanged  && entity.insurerId)  headerPromises.push(this.getQuoteInsurer({ id: entity.insurerId,  refresh: true }))

      this.$nextTick(() => {
        // The deep watchers have recomputed derived totals into the entity by now.
        // Re-baseline both snapshots from the settled entity so recomputed values
        // (e.g. rounding differences vs the broadcast totals) never read as unsaved
        // user edits — a residual diff here is what feeds the cross-tab save loop.
        this.setBaseSnapshot(_cloneDeep(this.entity))
        this.saveSnapshot(_cloneDeep(this.entity))

        this.$eventHub.$emit(EventHubTypes.EntitySaved)
        this.$nextTick(() => {
          this.$eventHub.$emit(`${EventHubTypes.EntitySaved}-QuoteHeader`)
          // Event-hub listeners may also touch the entity; lift the guard only after
          // they have all run.
          this.$nextTick(() => {
            this._isAutosaveRefreshing = false
          })
        })
        this.$eventHub.$emit(EventHubTypes.EntityReloaded)
        this._notifyPresenceUpdate(savedByName, source)
      })

      // When a header ID changed, load the new header data then re-emit so the
      // header sub-components (QuoteVehicle, QuoteCustomer, etc.) refresh their props.
      if (headerPromises.length) {
        Promise.all(headerPromises).then(() => {
          if (vehicleChanged)  this.vehicle  = _cloneDeep(this.currentVehicleSnapshot)
          if (customerChanged) this.customer = _cloneDeep(this.currentCustomerSnapshot)
          if (insurerChanged)  this.insurer  = _cloneDeep(this.currentInsurerSnapshot)
          this.$nextTick(() => {
            this.$eventHub.$emit(EventHubTypes.EntitySaved)
            this.$nextTick(() => {
              this.$eventHub.$emit(`${EventHubTypes.EntitySaved}-QuoteHeader`)
            })
          })
        })
      }
    },
    _onHeaderEntityUpdated({ updatedBy, updatedBySession, updatedByName }, entityIdField, fetchFn, snapshotGetter, targetProp) {
      if (this._isOwnPresenceEvent(updatedBySession, updatedBy)) return
      if (this.isPresenceEnabled && this.entity?.[entityIdField]) {
        fetchFn({ id: this.entity[entityIdField], refresh: true }).then(() => {
          this[targetProp] = _cloneDeep(this[snapshotGetter])
          this.$nextTick(() => {
            this.$eventHub.$emit(EventHubTypes.EntitySaved)
            this.$nextTick(() => {
              this.$eventHub.$emit(`${EventHubTypes.EntitySaved}-QuoteHeader`)
              this._notifyPresenceUpdate(updatedByName)
            })
          })
        })
        return
      }
      this._executePresenceReload(updatedByName)
    },
    _onCustomerUpdated(event) {
      this._onHeaderEntityUpdated(event, 'customerId', this.getQuoteCustomer, 'currentCustomerSnapshot', 'customer')
    },
    _onVehicleUpdated(event) {
      this._onHeaderEntityUpdated(event, 'vehicleId', this.getQuoteVehicle, 'currentVehicleSnapshot', 'vehicle')
    },
    async _executePresenceReload(updaterName, source) {
      if (this._presenceIsReloading) {
        this._presenceReloadPending = updaterName ?? true
        this._presenceReloadPendingSource = source
        return
      }
      this._presenceIsReloading = true
      this._presenceReloadPending = false
      try {
        await this.getEntity(true)
        await this.$nextTick()
        this.$eventHub.$emit(EventHubTypes.EntitySaved)
        this.$eventHub.$emit(EventHubTypes.EntityReloaded)
        this._notifyPresenceUpdate(updaterName, source)
      } catch (e) {
        console.error('[Presence] reload error', e)
      } finally {
        this._presenceIsReloading = false
        if (this._presenceReloadPending) {
          const pendingName = typeof this._presenceReloadPending === 'string'
            ? this._presenceReloadPending : null
          const pendingSource = this._presenceReloadPendingSource
          this._presenceReloadPending = false
          this._presenceReloadPendingSource = undefined
          this._executePresenceReload(pendingName, pendingSource)
        }
      }
    }
  }
}
